// mvp-returns §3.3 / §9.2 §8 — "Аналитик insight" tab for the return detail page.
// Buyer-centric (left) + product-centric (right) Phase-G insights, rendered only
// from data the existing API clients return (trust score + benchmark, this buyer's
// other returns, per-product active prevention alerts, comparable returns).

import {
  Alert, Anchor, Badge, Box, Card, Divider, Grid, Group, Progress, SimpleGrid,
  Skeleton, Stack, Table, Text, Title
} from "@mantine/core";
import {
  ArrowCounterClockwiseIcon, ScalesIcon, ShieldCheckIcon, WarningCircleIcon
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { i18n } from "@dehub/theme";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnAnalyticsApi, ReturnApi, ReturnPreventionAlertApi, TrustScoreApi } from "../../apis";
import type { IReturnDetail, IReturnItem, ResolutionType, ReturnReason, ReturnRequestStatus } from "../../apis/return";
import type { TrustScoreLevel } from "../../apis/trust-score";
import { currencyFormat } from "../../utils";
import RenderText from "../ui/render-text";
import { AlertSeverityBadge } from "./alert-severity-badge";
import { triggerLabel } from "./alert-trigger-label";
import { reasonLabel, ReasonBadge, ReturnStatusBadge } from "./atoms";
import { TrustLevelBadge } from "./trust-level-badge";

const resolutionLabel = (r?: ResolutionType | string | null) =>
  r ? i18n.t(`returns:insights.resolution.${r}`, { defaultValue: "-" }) : "-";

const LEVEL_COLOR: Record<TrustScoreLevel, string> = {
  EXCELLENT: "green",
  GOOD     : "teal",
  MODERATE : "yellow",
  AT_RISK  : "orange",
  HIGH_RISK: "red"
};

const num = (v: number | null | undefined, digits = 1) =>
  v == null ? "-" : Number(v).toFixed(digits);

// ── Public surface ───────────────────────────────────────────────────────────

interface IProps {
  ret: IReturnDetail;
  items: IReturnItem[];
}

export function ReturnInsightsPanel({ ret, items }: IProps) {
  const { t } = useTranslation("returns");
  const primaryProductId = items.find((it) => it.productId)?.productId ?? null;

  return (
    <Grid>
      {/* ── Left: buyer-centric ── */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Stack gap="md">
          {ret.buyerBusinessId ? (
            <BuyerTrustInsightCard buyerBusinessId={ret.buyerBusinessId} />
          ) : (
            <Alert color="gray">{t("insights.buyerUnknown")}</Alert>
          )}
          {ret.buyerBusinessId ? (
            <RepeatDetectorCard buyerBusinessId={ret.buyerBusinessId} excludeReturnId={ret.id} />
          ) : null}
        </Stack>
      </Grid.Col>

      {/* ── Right: product-centric ── */}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Stack gap="md">
          <ProductAlertsCard items={items} />
          <ComparableReturnsCard
            productId={primaryProductId}
            returnReason={ret.returnReason}
            excludeReturnId={ret.id}
          />
        </Stack>
      </Grid.Col>
    </Grid>
  );
}

// ── Buyer trust ───────────────────────────────────────────────────────────────

function BuyerTrustInsightCard({ buyerBusinessId }: { buyerBusinessId: string }) {
  const { t } = useTranslation("returns");
  const { data, isLoading, error } = useSWR(
    ["trust-score.get", buyerBusinessId],
    () => TrustScoreApi.get(buyerBusinessId),
    { revalidateOnFocus: false }
  );

  // Benchmark is secondary context — render when present, no skeleton/error noise.
  const { data: bench } = useSWR(
    ["trust-score.benchmark", buyerBusinessId],
    () => TrustScoreApi.benchmark(buyerBusinessId),
    { revalidateOnFocus: false }
  );

  return (
    <Card withBorder shadow="sm" radius="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <ShieldCheckIcon size={18} />
          <Title order={6}>{t("insights.trust.title")}</Title>
        </Group>
        <Anchor component={Link} to={`/returns/trust-scores/${buyerBusinessId}`} size="sm">
          {t("insights.detail")}
        </Anchor>
      </Group>

      {isLoading ? (
        <Skeleton h={120} />
      ) : error ? (
        <Text size="sm" c="dimmed">{t("insights.trust.notScored")}</Text>
      ) : data ? (
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <TrustLevelBadge level={data.level} size="md" />
              <Text size="xl" fw={700}>{Number(data.rawScore).toFixed(0)}</Text>
              <Text size="sm" c="dimmed">/ 100</Text>
            </Group>
          </Group>

          <Progress value={Math.max(0, Math.min(100, Number(data.rawScore)))} color={LEVEL_COLOR[data.level]} size="md" radius="sm" />

          {bench?.business ? (
            <Text size="sm" c="dimmed">
              {t("insights.trust.benchmark", { avg: Number(bench.tenant.averageRawScore).toFixed(0) })}{" "}
              <Text span fw={600}>{t("insights.trust.percentile", { pct: Number(bench.business.percentile).toFixed(0) })}</Text>{" "}
              {t("insights.trust.rank", { rank: bench.business.rank, total: bench.business.total })}
            </Text>
          ) : null}

          <Divider />

          {/* Components — only metrics the payload actually carries. */}
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            <Stack gap={2}>
              <Text size="sm" c="dimmed">{t("insights.trust.orders")}</Text>
              <Text size="lg" fw={500}>{data.totalOrders}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="sm" c="dimmed">{t("insights.trust.returnRate")}</Text>
              <Text size="lg" fw={500}>{num(data.returnRatePercent)}%</Text>
              <Text size="sm" c="dimmed">{t("insights.trust.returnsCount", { count: data.totalReturns })}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="sm" c="dimmed">{t("insights.trust.disputeRate")}</Text>
              <Text size="lg" fw={500}>{num(data.disputeRatePercent)}%</Text>
              <Text size="sm" c="dimmed">{t("insights.trust.disputesCount", { count: data.disputeCount })}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="sm" c="dimmed">{t("insights.trust.highValue")}</Text>
              <Text size="lg" fw={500}>{data.highValueReturnsCount}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="sm" c="dimmed">{t("insights.trust.cancelled")}</Text>
              <Text size="lg" fw={500}>{data.cancelledCount}</Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">{t("insights.noData")}</Text>
      )}
    </Card>
  );
}

// ── Repeat detector ───────────────────────────────────────────────────────────

function RepeatDetectorCard({
  buyerBusinessId,
  excludeReturnId
}: {
  buyerBusinessId: string;
  excludeReturnId: string;
}) {
  const { t } = useTranslation("returns");
  const { data, isLoading, error } = useSWR(
    ["returns.list.byBuyer", buyerBusinessId],
    () => ReturnApi.list({ buyerBusinessId, limit: 20, sortBy: "requestedAt", sortDir: "desc" }),
    { revalidateOnFocus: false }
  );

  const others = useMemo(
    () => (data?.returns ?? []).filter((r) => r.returnRequestId !== excludeReturnId),
    [data, excludeReturnId]
  );

  const pattern = useMemo(() => {
    const counts = new Map<ReturnReason, number>();
    for (const r of others) counts.set(r.returnReason, (counts.get(r.returnReason) ?? 0) + 1);
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([reason, n]) => `${reasonLabel(reason)} ${n}×`)
      .join(" · ");
  }, [others]);

  const last30 = useMemo(() => {
    const cutoff = dayjs().subtract(30, "day");
    return others.filter((r) => r.requestedAt && dayjs(r.requestedAt).isAfter(cutoff)).length;
  }, [others]);

  const shown = others.slice(0, 7);

  return (
    <Card withBorder shadow="sm" radius="md">
      <Group gap="xs" mb="sm">
        <ArrowCounterClockwiseIcon size={18} />
        <Title order={6}>{t("insights.repeat.title")}</Title>
      </Group>

      {isLoading ? (
        <Skeleton h={120} />
      ) : error ? (
        <Text size="sm" c="dimmed">{t("insights.repeat.loadError")}</Text>
      ) : others.length === 0 ? (
        <Text size="sm" c="dimmed">{t("insights.repeat.none")}</Text>
      ) : (
        <Stack gap="sm">
          <Group gap="md" wrap="wrap">
            <Text size="sm">
              <Text span fw={600}>{others.length}</Text> {t("insights.repeat.total")}
            </Text>
            <Text size="sm" c="dimmed">{t("insights.repeat.last30", { count: last30 })}</Text>
          </Group>
          {pattern ? <Text size="sm" c="dimmed">{t("insights.repeat.reasonFrequency", { pattern })}</Text> : null}

          <Divider />

          <Stack gap="xs">
            {shown.map((r) => (
              <Group key={r.returnRequestId} justify="space-between" wrap="nowrap" align="flex-start">
                <Stack gap={2}>
                  <Anchor component={Link} to={`/returns/${r.returnRequestId}`} size="sm" fw={600}>
                    {r.returnNumber}
                  </Anchor>
                  <Group gap="xs" wrap="wrap" align="center">
                    <ReasonBadge reason={r.returnReason} />
                    <Text size="sm" c="dimmed">{resolutionLabel(r.resolutionType)}</Text>
                  </Group>
                </Stack>
                <Stack gap={4} align="flex-end">
                  <Text size="sm" fw={500}>{currencyFormat(r.totalRequestedAmount)}</Text>
                  <ReturnStatusBadge status={r.status} size="xs" />
                </Stack>
              </Group>
            ))}
          </Stack>
          {others.length > shown.length ? (
            <Text size="sm" c="dimmed">{t("insights.repeat.more", { count: others.length - shown.length })}</Text>
          ) : null}
        </Stack>
      )}
    </Card>
  );
}

// ── Product prevention alerts ─────────────────────────────────────────────────

function ProductAlertsCard({ items }: { items: IReturnItem[] }) {
  const { t } = useTranslation("returns");
  const products = useMemo(() => {
    const map = new Map<string, { id: string; name: string; sku: string | null }>();
    for (const it of items) {
      if (it.productId && !map.has(it.productId)) {
        map.set(it.productId, { id: it.productId, name: it.productName, sku: it.sku ?? null });
      }
    }
    return Array.from(map.values());
  }, [items]);

  return (
    <Card withBorder shadow="sm" radius="md">
      <Group gap="xs" mb="sm">
        <WarningCircleIcon size={18} />
        <Title order={6}>{t("insights.product.title")}</Title>
      </Group>

      {products.length === 0 ? (
        <Text size="sm" c="dimmed">{t("insights.product.noItems")}</Text>
      ) : (
        <Stack gap="md">
          {products.map((p) => (
            <ProductAlertBlock key={p.id} product={p} />
          ))}
        </Stack>
      )}
    </Card>
  );
}

function ProductAlertBlock({ product }: { product: { id: string; name: string; sku: string | null } }) {
  const { t } = useTranslation("returns");
  // One `list` per distinct product (bounded by # of products on the return —
  // usually 1). We deliberately do NOT fetch each alert's detail (get) to surface
  // recommendedActions/reasonBreakdown — that would be N+1. See report.
  const { data, isLoading } = useSWR(
    ["alerts.list", product.id, "ACTIVE"],
    () => ReturnPreventionAlertApi.list({ productId: product.id, status: "ACTIVE", limit: 5 }),
    { revalidateOnFocus: false }
  );

  const alerts = data?.items ?? [];
  const total = data?.pagination.total ?? 0;

  return (
    <Box>
      <Group justify="space-between" wrap="nowrap" mb={alerts.length ? "xs" : 0}>
        <Stack gap={2}>
          <Text size="sm" fw={500}>{product.name}</Text>
          {product.sku ? <Text size="sm" c="dimmed">SKU: {product.sku}</Text> : null}
        </Stack>
        {isLoading ? (
          <Skeleton w={90} h={24} />
        ) : total === 0 ? (
          <Text size="sm" c="dimmed">{t("insights.product.noAlerts")}</Text>
        ) : (
          <Anchor component={Link} to={`/returns/prevention?productId=${product.id}`} size="sm" fw={600} c="red">
            {t("insights.product.activeCount", { count: total })}
          </Anchor>
        )}
      </Group>

      {alerts.length ? (
        <Stack gap="xs">
          {alerts.map((a) => (
            <Card key={a.id} withBorder radius="sm" padding="sm" bg="var(--mantine-color-red-0)">
              <Group justify="space-between" wrap="nowrap" mb={4}>
                <Group gap="xs" wrap="wrap" align="center">
                  <AlertSeverityBadge severity={a.severity} size="xs" />
                  <Text size="sm" fw={600}>{triggerLabel(a.triggerType)}</Text>
                </Group>
                {a.windowType ? <Text size="sm" c="dimmed">{a.windowType}</Text> : null}
              </Group>
              {a.message ? <Text size="sm">{a.message}</Text> : null}
              {a.triggerMetricValue != null || a.triggerThresholdValue != null ? (
                <Text size="sm" c="dimmed" mt={2}>
                  {t("insights.product.nowThreshold")} <Text span fw={600}>{num(a.triggerMetricValue)}</Text> · {t("insights.product.threshold")} {num(a.triggerThresholdValue)}
                </Text>
              ) : null}
            </Card>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

// ── Comparable returns ────────────────────────────────────────────────────────

function ComparableReturnsCard({
  productId,
  returnReason,
  excludeReturnId
}: {
  productId: string | null;
  returnReason: ReturnReason;
  excludeReturnId: string;
}) {
  const { t } = useTranslation("returns");
  const { data, isLoading, error } = useSWR(
    productId ? ["returns.comparable", productId, returnReason, excludeReturnId] : null,
    () => ReturnAnalyticsApi.comparable({ productId: productId as string, returnReason, excludeReturnId }),
    { revalidateOnFocus: false }
  );

  const rows = data?.comparables ?? [];

  return (
    <Card withBorder shadow="sm" radius="md">
      <Group gap="xs" mb="sm">
        <ScalesIcon size={18} />
        <Title order={6}>{t("insights.comparable.title")}</Title>
      </Group>
      <Text size="sm" c="dimmed" mb="sm">
        {t("insights.comparable.subtitle", { reason: reasonLabel(returnReason) })}
      </Text>

      {!productId ? (
        <Text size="sm" c="dimmed">{t("insights.comparable.noProduct")}</Text>
      ) : isLoading ? (
        <Skeleton h={120} />
      ) : error ? (
        <Text size="sm" c="dimmed">{t("insights.comparable.loadError")}</Text>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed">{t("insights.comparable.empty")}</Text>
      ) : (
        <Stack gap="sm">
          <Table verticalSpacing="xs" horizontalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>RET</Table.Th>
                <Table.Th>{t("insights.comparable.buyer")}</Table.Th>
                <Table.Th>{t("insights.comparable.returnPct")}</Table.Th>
                <Table.Th>{t("insights.comparable.resolution")}</Table.Th>
                <Table.Th>{t("insights.comparable.days")}</Table.Th>
                <Table.Th>{t("insights.comparable.status")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((c) => (
                <Table.Tr key={c.returnRequestId}>
                  <Table.Td>
                    <Anchor component={Link} to={`/returns/${c.returnRequestId}`} size="sm">
                      {c.returnNumber}
                    </Anchor>
                  </Table.Td>
                  <Table.Td><RenderText text={c.buyerBusinessName ?? "-"} size="sm" /></Table.Td>
                  <Table.Td><RenderText text={c.refundPercent != null ? `${c.refundPercent}%` : "-"} size="sm" /></Table.Td>
                  <Table.Td><RenderText text={resolutionLabel(c.resolutionType)} size="sm" /></Table.Td>
                  <Table.Td><RenderText text={c.resolvedTimeDays != null ? `${num(c.resolvedTimeDays)}` : "-"} size="sm" /></Table.Td>
                  <Table.Td><ReturnStatusBadge status={c.status as ReturnRequestStatus} size="xs" /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Badge variant="light" color="gray" size="lg" radius="xl">
            {t("insights.comparable.median", { pct: num(data?.medianRefundPercent, 0) })}
          </Badge>
        </Stack>
      )}
    </Card>
  );
}
